import React, { useCallback, useEffect, useState } from "react";
import {
	Box,
	Card,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Button,
	CardContent,
	TablePagination,
	SelectChangeEvent,
} from "@mui/material";
import { Grid } from "@material-ui/core";
import dayjs from "dayjs";
import checkBroken from "../../../assets/images/adminSection/check-broken.svg";
import clockForward from "../../../assets/images/adminSection/clock-forward.svg";
import fileAnalytics from "../../../assets/images/adminSection/file-analytics.svg";
import circleContained from "../../../assets/images/adminSection/x-circle-contained.svg";
import filter from "../../../assets/images/adminSection/filter.svg";
import CustomDatePicker from "../../../components/common/customDatePicker";
import BikeDiscountSearchableSelect from "../../layout/bikeDiscountSearchableSelect";
import BikeDiscountEmployeeSelect from "../../layout/bikeDiscountEmployeeSelect";
import { UserOption } from "../../../components/AutoCompleteUserSelect/AutoCompleteUserSelect";
import bikeDiscountService from "../../../services/bikeDiscountService";
import {
	BikeDiscountAdminDashboardFilters,
	BikeDiscountApplicationRow,
	BikeDiscountDashboardSummary,
} from "../../../types/bikeDiscount";
import { showToast } from "../../../components/common/toastNotification";
import {
	toUtcDateRangeEnd,
	toUtcDateRangeStart,
} from "../../../helper/getDateRangeFilter";

interface CardItem {
	label: string;
	count: number;
	icon: string;
}

interface FilterFormState {
	dateFrom: string;
	dateTo: string;
	employeeSearch: string;
	city: string;
	dealerCode: string;
	dealerName: string;
	modelCode: string;
	status: string;
}

const initialFilterForm: FilterFormState = {
	dateFrom: "",
	dateTo: "",
	employeeSearch: "",
	city: "",
	dealerCode: "",
	dealerName: "",
	modelCode: "",
	status: "",
};

const defaultDashboardFilters: BikeDiscountAdminDashboardFilters = {
	status: "All",
	search: "",
	sortColumn: "CreatedAtUtc",
	sortDirection: "ASC",
};

const statusOptions = [
	{ label: "All", value: "All" },
	{ label: "In Approval", value: "In Approval" },
	{ label: "In Booking & Delivery", value: "In Booking & Delivery" },
	{ label: "Rejected", value: "Rejected" },
	{ label: "Delivered", value: "Delivered" },
];

const getStatusColor = (statusName: string) => {
	const normalized = statusName?.toLowerCase() || "";
	if (normalized.includes("reject")) return "#FF3B30";
	if (normalized.includes("booking")) return "#34C759";
	if (normalized.includes("deliver")) return "#34C759";
	if (normalized.includes("approval") || normalized.includes("approv")) return "#FFCC00";
	return "#CCCCCC";
};

const formatCellValue = (value?: string | number | null) => {
	const text = value?.toString().trim();
	return text ? text : "-";
};

const mapApplicationRow = (app: Record<string, string | number | null | undefined>): BikeDiscountApplicationRow => ({
	applicationId: String(app.applicationId ?? app.applicationID ?? ""),
	employeeName: String(app.employeeName ?? ""),
	employeeCode: String(app.employeeCode ?? app.empCode ?? app.employeeId ?? ""),
	email: String(app.email ?? ""),
	city: String(app.city ?? ""),
	dealerName: String(app.dealerName ?? app.dealer ?? ""),
	modelCode: String(app.modelCode ?? ""),
	bookingNumber: String(app.bookingNumber ?? ""),
	chassisNumber: String(app.chassisNumber ?? ""),
	status: String(app.status ?? ""),
	statusName: String(app.statusName ?? app.status ?? ""),
});

const buildSummaryCards = (summary: BikeDiscountDashboardSummary): CardItem[] => {
	const totalCount = summary.total ?? 0;

	// New API keys: in_Approval + in_Booking_Delivery + delivered + rejected
	const hasNewKeys =
		summary.in_Approval !== undefined ||
		summary.in_Booking_Delivery !== undefined ||
		summary.delivered !== undefined;

	const inApprovalCount = hasNewKeys ? summary.in_Approval ?? 0 : summary.pending ?? 0;
	const inBookingDeliveryCount = hasNewKeys ? summary.in_Booking_Delivery ?? 0 : 0;
	const deliveredCount = hasNewKeys ? summary.delivered ?? 0 : summary.approved ?? 0;
	const rejectedCount = summary.rejected ?? 0;

	return [
		{ label: "All", count: totalCount, icon: fileAnalytics },
		{ label: "In Approval", count: inApprovalCount, icon: clockForward },
		{
			label: "In Booking & Delivery",
			count: inBookingDeliveryCount,
			icon: clockForward,
		},
		{ label: "Delivered", count: deliveredCount, icon: checkBroken },
		{ label: "Rejected", count: rejectedCount, icon: circleContained },
	];
};

const BikeDiscountDashboard: React.FC = () => {
	const [cardCount, setCardCount] = useState<CardItem[]>(
		buildSummaryCards({
			total: 0,
			approved: 0,
			rejected: 0,
			pending: 0,
		}),
	);
	const [tableData, setTableData] = useState<BikeDiscountApplicationRow[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [showFilters, setShowFilters] = useState(true);
	const [formData, setFormData] = useState<FilterFormState>(initialFilterForm);
	const [appliedFilters, setAppliedFilters] =
		useState<BikeDiscountAdminDashboardFilters>(defaultDashboardFilters);
	const [selectedEmployee, setSelectedEmployee] = useState<UserOption | null>(null);
	const [cityOptions, setCityOptions] = useState<{ label: string; value: string }[]>([]);
	const [dealerOptions, setDealerOptions] = useState<{ label: string; value: string }[]>([]);
	const [modelOptions, setModelOptions] = useState<{ label: string; value: string }[]>([]);
	const [isDealersLoading, setIsDealersLoading] = useState(false);

	const buildApiPayload = useCallback(
		(filters: BikeDiscountAdminDashboardFilters): BikeDiscountAdminDashboardFilters => ({
			status: filters.status || "All",
			search: filters.search ?? "",
			city: filters.city || null,
			dealerCode: filters.dealerCode || null,
			modelCode: filters.modelCode || null,
			dealerName: filters.dealerName || null,
			employeeCode: filters.employeeCode || null,
			department: null,
			dateFrom: filters.dateFrom || null,
			dateTo: filters.dateTo || null,
			page: page + 1,
			pageSize,
			sortColumn: filters.sortColumn || "CreatedAtUtc",
			sortDirection: filters.sortDirection || "ASC",
		}),
		[page, pageSize],
	);

	const fetchDashboardData = useCallback(
		async (filters: BikeDiscountAdminDashboardFilters = appliedFilters) => {
			try {
				const response = await bikeDiscountService.getApplicationDashboard(buildApiPayload(filters));

				const dashboardData = response?.data?.data ?? response?.data;
				const summary: BikeDiscountDashboardSummary = dashboardData?.summary ?? {
					total: 0,
					approved: 0,
					rejected: 0,
					pending: 0,
				};

				setCardCount(buildSummaryCards(summary));
				setTableData(
					(dashboardData?.applications || []).map((app: Record<string, string>) => mapApplicationRow(app)),
				);
				setTotalCount(dashboardData?.totalFilteredCount || 0);
			} catch (error) {
				console.error("Failed to fetch bike discount dashboard", error);
				setCardCount(
					buildSummaryCards({
						total: 0,
						approved: 0,
						rejected: 0,
						pending: 0,
					}),
				);
				setTableData([]);
				setTotalCount(0);
			}
		},
		[appliedFilters, buildApiPayload],
	);

	useEffect(() => {
		const fetchFilterOptions = async () => {
			try {
				const [citiesRes, modelsRes] = await Promise.all([
					bikeDiscountService.getCities(),
					bikeDiscountService.getModelCodes(),
				]);

				if (citiesRes?.data?.status === "Success" && citiesRes?.data?.cities) {
					setCityOptions(
						citiesRes.data.cities.map((city) => ({ label: city, value: city })),
					);
				}

				if (modelsRes?.data?.models) {
					setModelOptions(
						modelsRes.data.models.map((model) => ({
							label: `${model.modelName} (${model.modelCode})`,
							value: model.modelCode,
						})),
					);
				}
			} catch (error) {
				console.error("Failed to load filter options", error);
			}
		};

		fetchFilterOptions();
	}, []);

	const fetchDealersByCity = useCallback(async (city: string) => {
		if (!city) {
			setDealerOptions([]);
			return;
		}

		setIsDealersLoading(true);
		try {
			const response = await bikeDiscountService.getDealersByCity(city);
			if (response?.data?.status === "Success" && response?.data?.dealers) {
				setDealerOptions(
					response.data.dealers.map((dealer) => ({
						label: dealer.dealerName,
						value: dealer.dealerCode,
					})),
				);
			} else {
				setDealerOptions([]);
			}
		} catch (error) {
			console.error("Failed to load dealers", error);
			setDealerOptions([]);
		} finally {
			setIsDealersLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchDashboardData();
	}, [page, pageSize, appliedFilters]);

	const handleSelectChange = (event: SelectChangeEvent<string | number>) => {
		const { name, value } = event.target;

		if (name === "city") {
			const city = String(value);
			setFormData((prev) => ({
				...prev,
				city,
				dealerCode: "",
				dealerName: "",
			}));
			setDealerOptions([]);
			if (city) {
				fetchDealersByCity(city);
			}
			return;
		}

		if (name === "dealerCode") {
			const selectedDealer = dealerOptions.find((dealer) => dealer.value === value);
			setFormData((prev) => ({
				...prev,
				dealerCode: String(value),
				dealerName: selectedDealer?.label ?? "",
			}));
			return;
		}

		setFormData((prev) => ({ ...prev, [name]: String(value) }));
	};

	const handleSearch = () => {
		if (formData.dateFrom && formData.dateTo) {
			if (dayjs(formData.dateFrom).isAfter(dayjs(formData.dateTo))) {
				showToast("From date cannot be after to date.", "error");
				return;
			}
		}

		const nextFilters: BikeDiscountAdminDashboardFilters = {
			...defaultDashboardFilters,
			status: formData.status || "All",
			// employeeCode filter is driven by the autocomplete selection
			search: "",
			employeeCode: selectedEmployee?.id || null,
			city: formData.city || null,
			dealerCode: formData.dealerCode || null,
			dealerName: formData.dealerName || null,
			modelCode: formData.modelCode || null,
			dateFrom: formData.dateFrom ? toUtcDateRangeStart(formData.dateFrom) : null,
			dateTo: formData.dateTo ? toUtcDateRangeEnd(formData.dateTo) : null,
		};

		setAppliedFilters(nextFilters);
		setPage(0);
	};

	const handleClearFilters = () => {
		setFormData(initialFilterForm);
		setDealerOptions([]);
		setSelectedEmployee(null);
		setAppliedFilters(defaultDashboardFilters);
		setPage(0);
	};

	const handleDateFromChange = (date: dayjs.Dayjs | null) => {
		if (date?.isAfter(dayjs(), "day")) {
			showToast("From date cannot be a future date.", "error");
			return;
		}

		setFormData((prev) => {
			const dateFrom = date ? date.format("YYYY-MM-DD") : "";
			const shouldClearToDate =
				dateFrom && prev.dateTo && dayjs(prev.dateTo).isBefore(dayjs(dateFrom), "day");

			return {
				...prev,
				dateFrom,
				dateTo: shouldClearToDate ? "" : prev.dateTo,
			};
		});
	};

	const handleDateToChange = (date: dayjs.Dayjs | null) => {
		if (date?.isAfter(dayjs(), "day")) {
			showToast("To date cannot be a future date.", "error");
			return;
		}

		if (date && formData.dateFrom && date.isBefore(dayjs(formData.dateFrom), "day")) {
			showToast("To date cannot be before from date.", "error");
			return;
		}

		setFormData((prev) => ({
			...prev,
			dateTo: date ? date.format("YYYY-MM-DD") : "",
		}));
	};

	const disableFutureDate = (date: dayjs.Dayjs) => date.isAfter(dayjs(), "day");

	const disableInvalidToDate = (date: dayjs.Dayjs) =>
		date.isAfter(dayjs(), "day") ||
		(formData.dateFrom ? date.isBefore(dayjs(formData.dateFrom), "day") : false);

	return (
		<Box p={3}>
			<Card
				sx={{
					backgroundColor: "#212121",
					mb: 2,
					borderRadius: 0,
					boxShadow: "none",
				}}>
				<CardContent sx={{ py: 2, "&:last-child": { paddingBottom: 2 } }}>
					<Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "16px" }}>Bike Discount</Typography>
				</CardContent>
			</Card>

			<Box
				sx={{
					display: "flex",
					gap: 2,
					marginBottom: "16px",
					flexWrap: { xs: "wrap", md: "nowrap" },
				}}>
				{cardCount.map((item) => (
					<Box key={item.label} sx={{ flex: "1 1 0", minWidth: 0 }}>
						<Card
							sx={{
								backgroundColor: "#0A0A0A",
								color: "#fff",
								p: "16px",
								position: "relative",
								minHeight: 100,
							}}>
							<Box display="flex" flexDirection="column" justifyContent="space-between" mt={2}>
								<Typography className="f-16" sx={{ mb: 1 }}>
									{item.label}
								</Typography>
								<Typography variant="h4">{item.count}</Typography>
							</Box>
							<Box sx={{ position: "absolute", top: 40, right: 16 }}>
								<img src={item.icon} alt={item.label} />
							</Box>
						</Card>
					</Box>
				))}
			</Box>

			<Card
				sx={{
					backgroundColor: "#212121",
					mb: 2,
					borderRadius: 0,
					boxShadow: "none",
				}}>
				<CardContent>
					<Grid
						container
						style={{
							marginBottom: 8,
							alignItems: "center",
							justifyContent: "flex-end",
						}}>
						<Button
							className="filterBtn f-14"
							style={{
								minWidth: 160,
								maxWidth: 160,
								display: "flex",
								justifyContent: "space-between",
								padding: "0 15px",
							}}
							onClick={() => setShowFilters((prev) => !prev)}>
							<Typography>Filter</Typography>
							<img src={filter} alt="filter" />
						</Button>
					</Grid>

					{showFilters && (
						<Box
							sx={{
								backgroundColor: "#5D5D5D",
								padding: 2,
								borderRadius: "4px",
								marginBottom: 2,
							}}>
							<Grid container spacing={2}>
								<Grid item xs={6} md={2}>
									<CustomDatePicker
										name="dateFrom"
										label="From Date"
										backgroundColor="#5D5D5D"
										placeholder="Select"
										value={formData.dateFrom ? dayjs(formData.dateFrom) : null}
										shouldDisableDate={disableFutureDate}
										onChange={handleDateFromChange}
									/>
								</Grid>
								<Grid item xs={6} md={2}>
									<CustomDatePicker
										name="dateTo"
										label="To Date"
										backgroundColor="#5D5D5D"
										placeholder="Select"
										value={formData.dateTo ? dayjs(formData.dateTo) : null}
										shouldDisableDate={disableInvalidToDate}
										onChange={handleDateToChange}
									/>
								</Grid>
								<Grid item xs={12} md={4}>
									<BikeDiscountEmployeeSelect
										label="Employee Name / Code"
										placeholder="Search employee name or code"
										backgroundColor="#5D5D5D"
										value={selectedEmployee}
										onChange={setSelectedEmployee}
									/>
								</Grid>
								<Grid item xs={12} md={4}>
									<BikeDiscountSearchableSelect
										name="city"
										label="City"
										backgroundColor="#5D5D5D"
										value={formData.city}
										onChange={handleSelectChange}
										options={cityOptions}
										placeHolder="Select City"
										searchPlaceholder="Search city"
										resettable
									/>
								</Grid>
								<Grid item xs={12} md={4}>
									<BikeDiscountSearchableSelect
										name="dealerCode"
										label="Dealer"
										backgroundColor="#5D5D5D"
										value={formData.dealerCode}
										onChange={handleSelectChange}
										options={dealerOptions}
										placeHolder={
											isDealersLoading
												? "Loading dealers..."
												: formData.city
													? "Select Dealer"
													: "Select city first"
										}
										searchPlaceholder="Search dealer"
										disabled={!formData.city || isDealersLoading}
										loading={isDealersLoading}
										resettable
									/>
								</Grid>
								<Grid item xs={12} md={4}>
									<BikeDiscountSearchableSelect
										name="modelCode"
										label="Model Code"
										backgroundColor="#5D5D5D"
										value={formData.modelCode}
										onChange={handleSelectChange}
										options={modelOptions}
										placeHolder="Select Model Code"
										searchPlaceholder="Search model"
										resettable
									/>
								</Grid>
								<Grid item xs={12} md={4}>
									<BikeDiscountSearchableSelect
										name="status"
										label="Status"
										backgroundColor="#5D5D5D"
										value={formData.status}
										onChange={handleSelectChange}
										options={statusOptions}
										placeHolder="Select Status"
										searchable={false}
										resettable
									/>
								</Grid>
								<Grid
									item
									xs={12}
									style={{
										display: "flex",
										justifyContent: "flex-end",
										alignItems: "center",
										gap: 16,
									}}>
									<Button
										className="otherButton f-14"
										variant="contained"
										style={{
											borderRadius: 0,
											backgroundColor: "#5D5D5D",
											width: 140,
										}}
										onClick={handleSearch}>
										Search
									</Button>
									<Button
										className="otherButton f-14"
										variant="contained"
										style={{
											borderRadius: 0,
											backgroundColor: "#5D5D5D",
											width: 140,
										}}
										onClick={handleClearFilters}>
										Clear
									</Button>
								</Grid>
							</Grid>
						</Box>
					)}

					<TableContainer
						sx={{
							backgroundColor: "#212121",
							padding: 0,
							minHeight: "300px",
						}}>
						<Table>
							<TableHead>
								<TableRow>
									{[
										"Application ID",
										"Employee Name",
										"ECode",
										"Email",
										"City",
										"Dealer",
										"Model Code",
										"Booking Number",
										"Chassis Number",
										"Status",
									].map((header) => (
										<TableCell
											key={header}
											sx={{
												color: "#fff",
												background: "#0A0A0A",
												fontWeight: "600",
												whiteSpace: "nowrap",
                        py: 2.5,
                        borderBottom: "1px solid rgb(87, 87, 87)",
											}}>
											{header}
										</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{tableData.length === 0 ? (
									<TableRow>
										<TableCell colSpan={10} sx={{ color: "#fff", textAlign: "center", py: 2.5, borderBottom: "1px solid rgb(87, 87, 87)" }}>
											No data available.
										</TableCell>
									</TableRow>
								) : (
									tableData.map((row, index) => (
										<TableRow
											key={`${row.applicationId}-${index}`}
											sx={{
												// backgroundColor: index % 2 === 0 ? "#212121" : "#2A2A2A",
											}}
                      >
											<TableCell sx={{ color: "#fff", py: 2.5, borderBottom: "1px solid rgb(87, 87, 87)" }}>{formatCellValue(row.applicationId)}</TableCell>
											<TableCell sx={{ color: "#fff", py: 2.5, borderBottom: "1px solid rgb(87, 87, 87)" }}>{formatCellValue(row.employeeName)}</TableCell>
											<TableCell sx={{ color: "#fff", py: 2.5, borderBottom: "1px solid rgb(87, 87, 87)" }}>{formatCellValue(row.employeeCode)}</TableCell>
											<TableCell sx={{ color: "#fff", py: 2.5, borderBottom: "1px solid rgb(87, 87, 87)" }}>{formatCellValue(row.email)}</TableCell>
											<TableCell sx={{ color: "#fff", py: 2.5, borderBottom: "1px solid rgb(87, 87, 87)" }}>{formatCellValue(row.city)}</TableCell>
											<TableCell sx={{ color: "#fff", py: 2.5, borderBottom: "1px solid rgb(87, 87, 87)" }}>{formatCellValue(row.dealerName)}</TableCell>
											<TableCell sx={{ color: "#fff", py: 2.5, borderBottom: "1px solid rgb(87, 87, 87)" }}>{formatCellValue(row.modelCode)}</TableCell>
											<TableCell sx={{ color: "#fff", py: 2.5, borderBottom: "1px solid rgb(87, 87, 87)" }}>{formatCellValue(row.bookingNumber)}</TableCell>
											<TableCell sx={{ color: "#fff", py: 2.5, borderBottom: "1px solid rgb(87, 87, 87)" }}>{formatCellValue(row.chassisNumber)}</TableCell>
											<TableCell
												sx={{
													color: getStatusColor(row.statusName || row.status),
													fontWeight: 600,
                          borderBottom: "1px solid rgb(87, 87, 87)",
												}}>
												{formatCellValue(row.statusName || row.status)}
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<Grid
						style={{
							justifyContent: "flex-end",
							alignItems: "center",
							display: "flex",
							marginTop: 16,
							padding: 8,
						}}>
						<TablePagination
							rowsPerPageOptions={[5, 10, 25, 50, 100]}
							component="div"
							count={totalCount}
							rowsPerPage={pageSize}
							page={page}
							onPageChange={(_, newPage) => setPage(newPage)}
							onRowsPerPageChange={(e) => {
								setPageSize(parseInt(e.target.value, 10));
								setPage(0);
							}}
							sx={{
								color: "#ffffff",
								"& .MuiSelect-icon": { color: "#ffffff" },
								"& .MuiSvgIcon-root": { color: "#ffffff" },
							}}
						/>
					</Grid>
				</CardContent>
			</Card>
		</Box>
	);
};

export default BikeDiscountDashboard;
